import React from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
  slidingToggleButtonNoBorder,
  slidingToggleGroupNoBorder
} from '../../../../components/SlidingToggleButton/noBorderStyles';

/**
 * One closed question on 補資料, drawn as the sliding toggle the rest of the app
 * already uses for exactly this shape — `Pro | Lite` on the home page, and the
 * three DISC pickers (DISCPersonalityPicker, DISCDialogComponent, the two
 * ChatGPT drawer pickers). All of them build on
 * `SlidingToggleButton/noBorderStyles`, which is what this imports; MUI's
 * ToggleButtonGroup underneath means the selection semantics are the
 * component's rather than this card's own reimplementation of them.
 *
 * Not DISCPersonalityPicker itself, close as that one is: it answers two fixed
 * axes and brings its own heading and its own DISC-letter callout, both of which
 * this card already has one of. What is shared is the part worth sharing.
 *
 * THREE states, not two. Every question here can also be unanswered — which is
 * the state the whole card exists to show — so the pill is hidden outright when
 * nothing is chosen rather than parked on one of the answers. That is the same
 * `index === -1` treatment DISCPersonalityPicker gives it, and it is what makes
 * a switch honest about a question nobody has answered yet.
 *
 * `exclusive` also gives the clear-by-tapping-the-chosen-answer behaviour for
 * free: MUI reports `null` when the selected button is tapped again, which is
 * the only way back to "never answered".
 */
export type FactToggleOption = {
  /** Written when this answer is picked, and the button's `data-choice`. */
  value: string;
  /** What the button shows — a word, a numeral, or a glyph. */
  label: React.ReactNode;
  /** Required when `label` is a glyph, which leaves the button with no name. */
  ariaLabel?: string;
};

type FactToggleProps = {
  options: FactToggleOption[];
  /** `null` when the question is unanswered. */
  value: string | null;
  onChange: (next: string | null) => void;
  ariaLabel?: string;
  optionTestId?: string;
  /** Mirrors the old buttons' `data-field` / `data-axis` hooks. */
  dataField?: string;
  dataAxis?: string;
  className?: string;
  /** Every option, e.g. when the answer has nowhere to be saved. */
  disabled?: boolean;
};

// One colour for the chosen answer, whichever question it belongs to. The
// buttons used to paint 是 amber, a tier its badge's own hue and everything else
// indigo — a private code for "which kind of answer is this", which nobody was
// ever told and which cost the card its only strong signal. A single filled
// pill in the app's own blue is read as "this one" by everybody, immediately.
const SELECTED_FILL = '#3966f8';

const groupSx = {
  position: 'relative',
  display: 'flex',
  width: '100%',
  padding: '3px',
  borderRadius: '9px',
  backgroundColor: '#eef1f6',
  zIndex: 0,
  ...slidingToggleGroupNoBorder
};

const buttonSx = {
  ...slidingToggleButtonNoBorder,
  flex: '1 1 0',
  minWidth: 0,
  height: 30,
  padding: '0 8px',
  borderRadius: '6px !important',
  backgroundColor: 'transparent !important',
  color: '#475569',
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.2,
  textTransform: 'none !important',
  // Above the pill, so the label reads on top of it rather than under.
  zIndex: 2,
  transition: 'color 0.2s ease',
  WebkitTapHighlightColor: 'transparent',
  // Glyph answers inherit this, so a chosen ♦ turns white with its label would.
  '& svg': { display: 'block' },
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.6) !important' },
  '&.Mui-selected, &.Mui-selected:hover': {
    color: '#ffffff',
    backgroundColor: 'transparent !important'
  }
};

const FactToggle = ({
  options,
  value,
  onChange,
  ariaLabel,
  optionTestId,
  dataField,
  dataAxis,
  className,
  disabled
}: FactToggleProps) => {
  const index = options.findIndex(option => option.value === value);
  const count = options.length || 1;
  const unanswered = index === -1;

  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      aria-label={ariaLabel}
      className={className}
      disabled={disabled}
      onChange={(_event: unknown, next: string | null) => onChange(next)}
      sx={groupSx}
    >
      {/* The pill. A sibling of the buttons rather than a background on the
          chosen one, so moving between answers slides instead of blinking. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 3,
          bottom: 3,
          left: 3,
          width: `calc((100% - 6px) / ${count})`,
          transform: `translateX(${Math.max(index, 0) * 100}%)`,
          opacity: unanswered ? 0 : 1,
          visibility: unanswered ? 'hidden' : 'visible',
          backgroundColor: SELECTED_FILL,
          borderRadius: 6,
          boxShadow: '0 2px 6px rgba(57, 102, 248, 0.24)',
          transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease, visibility 0.2s ease',
          zIndex: 1
        }}
      />
      {options.map(option => (
        <ToggleButton
          key={option.value}
          value={option.value}
          disableRipple
          aria-label={option.ariaLabel}
          data-testid={optionTestId}
          data-choice={option.value}
          data-field={dataField}
          data-axis={dataAxis}
          sx={buttonSx}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export default FactToggle;
